import { useLazyQuery, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { DatetimeChangeEventDetail, IonDatetimeCustomEvent } from '@ionic/core';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { utcToZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import DateTime from '@components/fields/DateTime';
import ExpertSelect from '@components/fields/ExpertSelect';
import FormButtons from '@components/fields/FormButtons';
import Form from '@components/forms/Form';
import styles from '@components/forms/OutOfOfficeForm.module.css';
import Icon from '@components/text/Icon';
import {
  GetOutOfOfficePeriodConflictsDocument,
  GetOutOfOfficePeriodConflictsQuery,
  GetOutOfOfficePeriodConflictsQueryVariables,
  GetOutOfOfficePeriodsQuery,
  OutOfOfficePeriodCreateDocument,
  OutOfOfficePeriodCreateMutation,
  OutOfOfficePeriodCreateMutationVariables,
  OutOfOfficePeriodUpdateDocument,
  OutOfOfficePeriodUpdateMutation,
  OutOfOfficePeriodUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type OutOfOfficePeriod = NonNullable<
  NonNullable<GetOutOfOfficePeriodsQuery['outOfOfficePeriods']['edges']>[number]
>['node'];

type FormData = {
  backupExpertId: string;
  endTime: string;
  expertId: string;
  id?: string;
  startTime: string;
};

type Props = {
  onCancel: () => void;
  onSuccess: () => void;
  outOfOfficePeriod?: OutOfOfficePeriod;
};

const OutOfOfficeForm = ({ onCancel, onSuccess, outOfOfficePeriod }: Props) => {
  const intl = useIntl();

  const timeAsStringInExpertZoneOrDefault = useCallback(
    (date: Date | null | undefined, expertTimezone: string | null | undefined) => {
      if (date && expertTimezone) {
        const zonedTime = utcToZonedTime(date, expertTimezone);
        return new Date(
          Date.UTC(
            zonedTime.getFullYear(),
            zonedTime.getMonth(),
            zonedTime.getDate(),
            zonedTime.getHours(),
            zonedTime.getMinutes()
          )
        ).toISOString();
      }
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), today.getHours(), 0, 0).toISOString();
    },
    []
  );

  const initialValues = {
    backupExpertId: outOfOfficePeriod?.backupExpert.id ?? '',
    endTime: timeAsStringInExpertZoneOrDefault(outOfOfficePeriod?.endTime, outOfOfficePeriod?.expert.timezone),
    expertId: outOfOfficePeriod?.expert.id ?? '',
    id: outOfOfficePeriod?.id,
    startTime: timeAsStringInExpertZoneOrDefault(outOfOfficePeriod?.startTime, outOfOfficePeriod?.expert.timezone)
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createOutOfOfficePeriod, { client }] = useMutation<
    OutOfOfficePeriodCreateMutation,
    OutOfOfficePeriodCreateMutationVariables
  >(OutOfOfficePeriodCreateDocument, {
    update: () => {
      client.resetStore();
    }
  });

  const [updateOutOfOfficePeriod] = useMutation<
    OutOfOfficePeriodUpdateMutation,
    OutOfOfficePeriodUpdateMutationVariables
  >(OutOfOfficePeriodUpdateDocument);

  const [getOutOfOfficePeriodConflicts, getOutOfOfficePeriodConflictsQuery] = useLazyQuery<
    GetOutOfOfficePeriodConflictsQuery,
    GetOutOfOfficePeriodConflictsQueryVariables
  >(GetOutOfOfficePeriodConflictsDocument);

  const schema = yup
    .object()
    .shape({
      backupExpertId: yup.string().required(intl.formatMessage({ id: 'errors.outOfOffice.backupExpertId.blank' })),
      endTime: yup.string().required(intl.formatMessage({ id: 'errors.outOfOffice.endTime.blank' })),
      expertId: yup.string().required(intl.formatMessage({ id: 'errors.outOfOffice.expertId.blank' })),
      startTime: yup.string().required(intl.formatMessage({ id: 'errors.outOfOffice.startTime.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const endTime = form.watch('endTime');
  const expertId = form.watch('expertId');
  const startTime = form.watch('startTime');

  const handleStartTimeChange = useCallback(
    (event: IonDatetimeCustomEvent<DatetimeChangeEventDetail>) => {
      if (event.detail.value && endTime < event.detail.value) {
        form.setValue('endTime', event.detail.value as string);
      }
      form.setValue('startTime', event.detail.value as string);
    },
    [endTime, form]
  );

  const handleEndTimeChange = useCallback(
    (event: IonDatetimeCustomEvent<DatetimeChangeEventDetail>) => {
      if (event.detail.value && event.detail.value < startTime) {
        form.setValue('startTime', event.detail.value as string);
      }
      form.setValue('endTime', event.detail.value as string);
    },
    [form, startTime]
  );

  useEffect(() => {
    if (endTime && expertId && startTime) {
      getOutOfOfficePeriodConflicts({
        variables: { endTime, expertId, startTime }
      });
    }
  }, [endTime, expertId, form, getOutOfOfficePeriodConflicts, startTime]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    const variables = {
      ...form.getValues(),
      endTime: form
        .getValues()
        .endTime.replace('Z', '')
        .replace(/[+-]\d\d:\d\d$/, ''),
      startTime: form
        .getValues()
        .startTime.replace('Z', '')
        .replace(/[+-]\d\d:\d\d$/, '')
    };

    if (outOfOfficePeriod?.id) {
      return updateOutOfOfficePeriod({
        variables: {
          ...variables,
          id: outOfOfficePeriod.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.outOfOfficePeriodUpdate,
          intl,
          onSuccess: () => {
            if (response.data?.outOfOfficePeriodUpdate?.outOfOfficePeriod) {
              onSuccess();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.outOfOffice'
        });
      });
    }
    return createOutOfOfficePeriod({ variables }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.outOfOfficePeriodCreate,
        intl,
        onSuccess: () => {
          if (response.data?.outOfOfficePeriodCreate?.outOfOfficePeriod) {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.outOfOffice'
      });
    });
  }, [createOutOfOfficePeriod, form, intl, onSuccess, outOfOfficePeriod?.id, updateOutOfOfficePeriod]);

  const isWeekday = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const utcDay = date.getUTCDay();

    return utcDay !== 0 && utcDay !== 6;
  }, []);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      {getOutOfOfficePeriodConflictsQuery.data &&
        (getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.consultConflicts > 0 ||
          getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.minAsiaCoverage === 0 ||
          getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.minEuropeCoverage === 0 ||
          getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.minExperts === 0) && (
          <IonGrid className={`${styles.alertBox} ion-no-padding ion-no-margin`}>
            <IonRow className="ion-align-items-center">
              <IonCol className={styles.warning} size="auto">
                <Icon ariaLabelKey="accessibility.ariaLabels.alert" icon="exclamationTriangle" />
              </IonCol>
              <IonCol className={styles.alertInfo}>
                <div>
                  <FormattedMessage id="forms.outOfOffice.alerts.heading" />
                </div>
                <ul>
                  {getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.consultConflicts > 0 && (
                    <li>
                      <FormattedMessage
                        id="forms.outOfOffice.alerts.consultConflicts"
                        values={{
                          count: getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.consultConflicts
                        }}
                      />
                    </li>
                  )}
                  {getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.minAsiaCoverage === 0 && (
                    <li>
                      <FormattedMessage id="forms.outOfOffice.alerts.noAsiaCoverage" />
                    </li>
                  )}
                  {getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.minEuropeCoverage === 0 && (
                    <li>
                      <FormattedMessage id="forms.outOfOffice.alerts.noEuropeCoverage" />
                    </li>
                  )}
                  {getOutOfOfficePeriodConflictsQuery.data.outOfOfficePeriodConflicts.minExperts === 0 && (
                    <li>
                      <FormattedMessage id="forms.outOfOffice.alerts.noExperts" />
                    </li>
                  )}
                </ul>
              </IonCol>
            </IonRow>
          </IonGrid>
        )}

      <BaseErrors errors={baseErrors} />
      <ExpertSelect<FormData>
        allowAny={false}
        control={form.control}
        error={form.formState.errors.expertId}
        initialOption={{ label: outOfOfficePeriod?.expert.fullName, value: outOfOfficePeriod?.expert.id ?? null }}
        label={intl.formatMessage({ id: 'forms.outOfOffice.expertLabel' })}
        name="expertId"
      />
      <DateTime
        control={form.control}
        error={form.formState.errors.startTime}
        isDateEnabled={isWeekday}
        label={intl.formatMessage({ id: 'forms.outOfOffice.startTimeLabel' })}
        minuteValues="0,15,30,45"
        name="startTime"
        onIonChange={handleStartTimeChange}
        required
        yearValues="2023,2024,2025,2026"
      />
      <DateTime
        control={form.control}
        error={form.formState.errors.endTime}
        isDateEnabled={isWeekday}
        label={intl.formatMessage({ id: 'forms.outOfOffice.endTimeLabel' })}
        min={startTime}
        minuteValues="0,15,30,45"
        name="endTime"
        onIonChange={handleEndTimeChange}
        required
        yearValues="2023,2024,2025,2026"
      />
      <ExpertSelect<FormData>
        allowAny={false}
        control={form.control}
        error={form.formState.errors.backupExpertId}
        initialOption={{
          label: outOfOfficePeriod?.backupExpert.fullName,
          value: outOfOfficePeriod?.backupExpert.id ?? null
        }}
        label={intl.formatMessage({ id: 'forms.outOfOffice.backupExpertLabel' })}
        name="backupExpertId"
      />
      <FormButtons hookForm={form} onCancel={onCancel} />
    </Form>
  );
};

export default OutOfOfficeForm;
